import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "@/components/Sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Link2, Phone, MessageCircle, Contact, Copy, Check, ArrowLeft, FileImage, FileCode, Palette } from "lucide-react";
import { useStyledQR } from "@/components/StyledQRCode";
import QRStylePanel from "@/components/QRStylePanel";
import { DEFAULT_STYLE } from "@/lib/presetLogos";
import { toast, Toaster } from "sonner";
import { motion } from "framer-motion";

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL ||
  (process.env.NODE_ENV !== "production" ? "http://localhost:8000" : "");
const API = `${BACKEND_URL}/api`;

function buildStarString(rating, maxStars = 5) {
  const safeMax = Number.isFinite(maxStars) ? Math.max(1, Math.min(10, Math.round(maxStars))) : 5;
  const safeRating = Number.isFinite(rating) ? Math.max(0, Math.min(safeMax, Math.round(rating))) : safeMax;
  return "★".repeat(safeRating) + "☆".repeat(safeMax - safeRating);
}

const qrTypes = [
  { id: "url", label: "URL", icon: Link2, placeholder: "https://example.com" },
  { id: "phone", label: "Phone", icon: Phone, placeholder: "+91 98765 43210" },
  { id: "whatsapp", label: "WhatsApp", icon: MessageCircle, placeholder: "+91 98765 43210" },
  { id: "vcard", label: "vCard", icon: Contact, placeholder: "" },
];

export default function QRGenerator() {
  const navigate = useNavigate();
  const [activeType, setActiveType] = useState("url");
  const [content, setContent] = useState("");
  const [label, setLabel] = useState("");
  const [creating, setCreating] = useState(false);
  const [createdQR, setCreatedQR] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showStyle, setShowStyle] = useState(false);
  const [qrStyle, setQrStyle] = useState({ ...DEFAULT_STYLE });

  // vCard fields
  const [vcardName, setVcardName] = useState("");
  const [vcardEmail, setVcardEmail] = useState("");
  const [vcardPhone, setVcardPhone] = useState("");
  const [vcardCompany, setVcardCompany] = useState("");
  const [vcardTitle, setVcardTitle] = useState("");

  const buildVCard = useCallback(() => {
    return `BEGIN:VCARD\nVERSION:3.0\nFN:${vcardName}\nTEL:${vcardPhone}\nEMAIL:${vcardEmail}\nORG:${vcardCompany}\nTITLE:${vcardTitle}\nEND:VCARD`;
  }, [vcardName, vcardEmail, vcardPhone, vcardCompany, vcardTitle]);

  const getContent = () => (activeType === "vcard" ? buildVCard() : content);
  const getRedirectUrl = (shortCode) => (BACKEND_URL ? `${BACKEND_URL}/api/r/${shortCode}` : `${window.location.origin}/api/r/${shortCode}`);

  const qrData = createdQR ? getRedirectUrl(createdQR.short_code) : (getContent() || "https://qrhub.app");
  const { ref: qrRef, downloadPNG, downloadSVG } = useStyledQR({
    data: qrData,
    style: qrStyle,
    size: 220,
  });

  const handleCreate = async () => {
    const finalContent = getContent();
    if (!finalContent.trim()) {
      toast.error("Please enter content for the QR code");
      return;
    }
    setCreating(true);
    try {
      const stylePayload = { ...qrStyle };
      // Don't send logo_base64 if it's a preset
      if (stylePayload.logo_preset) stylePayload.logo_base64 = null;

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000);

      const res = await fetch(`${API}/qr/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        signal: controller.signal,
        body: JSON.stringify({
          qr_type: activeType,
          content: finalContent,
          label: label || null,
          style: stylePayload,
        }),
      });
      clearTimeout(timeoutId);
      if (!res.ok) {
        const err = await res.json();
        toast.error(err.detail || "Failed to create QR code");
        return;
      }
      const data = await res.json();
      setCreatedQR(data);
      toast.success("QR code created successfully");
    } catch (e) {
      if (e?.name === "AbortError") {
        toast.error("Request timed out. Is the backend running?");
      } else {
        toast.error("Network error");
      }
    } finally {
      setCreating(false);
    }
  };

  const handleCopyLink = () => {
    if (!createdQR) return;
    navigator.clipboard.writeText(getRedirectUrl(createdQR.short_code));
    setCopied(true);
    toast.success("Link copied");
    setTimeout(() => setCopied(false), 2000);
  };

  const resetForm = () => {
    setCreatedQR(null);
    setContent("");
    setLabel("");
    setVcardName("");
    setVcardEmail("");
    setVcardPhone("");
    setVcardCompany("");
    setVcardTitle("");
    setQrStyle({ ...DEFAULT_STYLE });
  };

  const inputClass = "w-full border border-zinc-200 rounded-md px-4 py-3 text-sm focus:border-[#0047FF] focus:ring-1 focus:ring-[#0047FF] transition-all bg-white outline-none";
  const labelClass = "block text-sm font-bold text-zinc-950 mb-2";

  return (
    <div className="flex h-screen overflow-hidden bg-zinc-50" data-testid="qr-generator-page">
      <Sidebar />
      <main className="flex-1 overflow-y-auto">
        <Toaster richColors position="top-right" />
        <div className="p-6 lg:p-8">
          <div className="mb-8 pl-12 lg:pl-0">
            <button
              onClick={() => navigate("/dashboard")}
              className="flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-950 transition-colors mb-4"
              data-testid="back-to-dashboard"
            >
              <ArrowLeft className="h-4 w-4" /> Back to Dashboard
            </button>
            <h1
              className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-950"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            >
              Create QR Code
            </h1>
            <p className="mt-1 text-sm text-zinc-500">Generate a styled, dynamic QR code</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Left: Content Form */}
            <motion.div
              className="lg:col-span-4"
              initial={{ opacity: 0, x: -12 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Card className="border-zinc-200 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base font-bold" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                    QR Content
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-5">
                  <Tabs value={activeType} onValueChange={(v) => { setActiveType(v); setContent(""); setCreatedQR(null); }}>
                    <TabsList className="w-full grid grid-cols-4 bg-zinc-100 rounded-lg p-1" data-testid="qr-type-tabs">
                      {qrTypes.map((t) => (
                        <TabsTrigger
                          key={t.id}
                          value={t.id}
                          data-testid={`qr-type-${t.id}`}
                          className="flex items-center gap-1 text-[10px] font-bold data-[state=active]:bg-white data-[state=active]:text-zinc-950 data-[state=active]:shadow-sm rounded-md"
                        >
                          <t.icon className="h-3 w-3" />
                          {t.label}
                        </TabsTrigger>
                      ))}
                    </TabsList>

                    {qrTypes.filter((t) => t.id !== "vcard").map((t) => (
                      <TabsContent key={t.id} value={t.id} className="mt-4">
                        <div>
                          <label className={labelClass}>
                            {t.id === "url" ? "Destination URL" : t.id === "phone" ? "Phone Number" : "WhatsApp Number"}
                          </label>
                          <input
                            data-testid={`input-${t.id}`}
                            type="text"
                            value={content}
                            onChange={(e) => setContent(e.target.value)}
                            placeholder={t.placeholder}
                            className={inputClass}
                          />
                        </div>
                      </TabsContent>
                    ))}

                    <TabsContent value="vcard" className="mt-4 space-y-3">
                      <div>
                        <label className={labelClass}>Full Name</label>
                        <input data-testid="input-vcard-name" type="text" value={vcardName} onChange={(e) => setVcardName(e.target.value)} placeholder="John Doe" className={inputClass} />
                      </div>
                      <div>
                        <label className={labelClass}>Email</label>
                        <input data-testid="input-vcard-email" type="email" value={vcardEmail} onChange={(e) => setVcardEmail(e.target.value)} placeholder="john@co.com" className={inputClass} />
                      </div>
                      <div>
                        <label className={labelClass}>Phone</label>
                        <input data-testid="input-vcard-phone" type="text" value={vcardPhone} onChange={(e) => setVcardPhone(e.target.value)} placeholder="+91 98765 43210" className={inputClass} />
                      </div>
                      <div>
                        <label className={labelClass}>Company</label>
                        <input data-testid="input-vcard-company" type="text" value={vcardCompany} onChange={(e) => setVcardCompany(e.target.value)} placeholder="Acme Inc" className={inputClass} />
                      </div>
                      <div>
                        <label className={labelClass}>Job Title</label>
                        <input data-testid="input-vcard-title" type="text" value={vcardTitle} onChange={(e) => setVcardTitle(e.target.value)} placeholder="CEO" className={inputClass} />
                      </div>
                    </TabsContent>
                  </Tabs>

                  <div>
                    <label className={labelClass}>Label (optional)</label>
                    <input
                      data-testid="input-label"
                      type="text"
                      value={label}
                      onChange={(e) => setLabel(e.target.value)}
                      placeholder="e.g. Business Card, Menu Link"
                      className={inputClass}
                    />
                  </div>

                  {!createdQR ? (
                    <Button
                      data-testid="generate-qr-button"
                      onClick={handleCreate}
                      disabled={creating}
                      className="w-full bg-[#0047FF] text-white hover:bg-[#0033CC] font-bold rounded-lg h-12 active:scale-[0.98] transition-all"
                    >
                      {creating ? "Creating..." : "Generate QR Code"}
                    </Button>
                  ) : (
                    <Button
                      data-testid="create-another-button"
                      onClick={resetForm}
                      variant="outline"
                      className="w-full font-bold rounded-lg h-12 border-2"
                    >
                      Create Another
                    </Button>
                  )}
                </CardContent>
              </Card>
            </motion.div>

            {/* Center: Style Panel */}
            <motion.div
              className="lg:col-span-4"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.05 }}
            >
              <Card className="border-zinc-200 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2">
                    <Palette className="h-4 w-4 text-[#0047FF]" />
                    <CardTitle className="text-base font-bold" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                      Customize Style
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <QRStylePanel style={qrStyle} onChange={setQrStyle} />
                </CardContent>
              </Card>
            </motion.div>

            {/* Right: Preview */}
            <motion.div
              className="lg:col-span-4"
              initial={{ opacity: 0, x: 12 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: 0.1 }}
            >
              <Card className="border-zinc-200 shadow-[0_2px_8px_rgba(0,0,0,0.04)] sticky top-8">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base font-bold" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                    QR Preview
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-col items-center">
                    <div
                      className="rounded-2xl p-4 mb-4 border-2 border-zinc-100"
                      style={{ backgroundColor: qrStyle.bg_color || "#FFFFFF" }}
                      data-testid="qr-code-preview"
                    >
                      <div className="flex flex-col items-center">
                        <div ref={qrRef} />
                        {qrStyle.show_review_stars && (
                          <div
                            className="mt-3 text-sm font-black tracking-wide"
                            style={{ color: qrStyle.fg_color || "#09090B" }}
                            data-testid="qr-preview-stars"
                          >
                            {buildStarString(qrStyle.review_rating, 5)}
                          </div>
                        )}
                        {(qrStyle.bottom_text || "").trim() && (
                          <div
                            className="mt-2 text-xs font-bold text-center max-w-[220px] break-words"
                            style={{ color: qrStyle.fg_color || "#09090B" }}
                            data-testid="qr-preview-bottom-text"
                          >
                            {qrStyle.bottom_text}
                          </div>
                        )}
                      </div>
                    </div>

                    {createdQR && (
                      <div className="w-full space-y-3">
                        <div className="bg-zinc-50 rounded-lg p-3 flex items-center justify-between">
                          <code className="text-[10px] text-zinc-600 font-mono truncate flex-1 mr-2">
                            {getRedirectUrl(createdQR.short_code)}
                          </code>
                          <button
                            data-testid="copy-qr-link"
                            onClick={handleCopyLink}
                            className="flex-shrink-0 rounded-md p-1.5 hover:bg-zinc-200 transition-colors"
                          >
                            {copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4 text-zinc-500" />}
                          </button>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            data-testid="download-png-button"
                            onClick={() => downloadPNG(`qr-${createdQR.short_code}`)}
                            className="flex-1 bg-zinc-950 text-white hover:bg-zinc-800 font-bold rounded-lg active:scale-[0.98]"
                          >
                            <FileImage className="mr-1.5 h-4 w-4" />
                            PNG
                          </Button>
                          <Button
                            data-testid="download-svg-button"
                            onClick={() => downloadSVG(`qr-${createdQR.short_code}`)}
                            variant="outline"
                            className="flex-1 font-bold rounded-lg border-2 active:scale-[0.98]"
                          >
                            <FileCode className="mr-1.5 h-4 w-4" />
                            SVG
                          </Button>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs font-bold uppercase">{createdQR.qr_type}</Badge>
                          <span className="text-xs text-zinc-400">{createdQR.label}</span>
                        </div>
                      </div>
                    )}

                    {!createdQR && (
                      <p className="text-xs text-zinc-400 text-center">
                        Live preview updates as you type and customize
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </main>
    </div>
  );
}
